import { Box, Button, Modal, Text } from '@fozito/common-ui';
import { Form, Input, Spin } from 'antd';
import FormItemValue from 'components/FormItemValue';
import { debounce, delay } from 'lodash';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierGiftCardPayment } from 'modules/POS/services/types/payment';
import moment from 'moment';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import { PAYMENT_TYPE } from '../../cashier.constants';
import { InputWithKeyboardStyled, useDollarAmountInputRef } from '../../components/DollarAmount/Input';
import DollarAmountKeyboard from '../../components/DollarAmount/Keyboard';
import NumberPadV2 from '../../components/NumberPad';
import { FooterActions } from '../../features/SellGift/ModalSelfGift/styles';
import useGetInfoGiftCard from '../../hooks/useGetInfoGiftCard';
import { useSocketContext } from '../../hooks/useSocket';
import { ICustomerPaymentDetail, TypePaymentActors, TypePaymentPassData } from '../../socket.interface';
import qrScanIcon from './QR-scan.svg';
import giftCardBgIcon from './gift-card-bg-icon.svg';
import { cashierBtnMultiPayV3 } from '../MultiPayment';
import { IGiftCardPaymentValue } from '../../cashier.interface';
import { cashierCtrl } from '../../cashier.controller';
import useListenRequestSync from '../../hooks/useListenRequestSync';
export type ICashierGiftCardPaymentMethod = (billId: string, payment: ICashierGiftCardPayment) => Promise<boolean>;
type Props = {
  enable?: boolean;
  onPayment: ICashierGiftCardPaymentMethod;
};
type Ref = {
  open: (billId: string) => void;
};
export const useModalGiftCardPaymentRef = () => useRef<Ref>(null);
const ModalGiftCardPayment = forwardRef<Ref, Props>(({ onPayment }, ref) => {
  const dollarRef = useDollarAmountInputRef();
  const [form] = Form.useForm();
  const [billId, setOpen] = useState<string>('');
  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
  };
  const giftCard = useGetInfoGiftCard(form, dollarRef, () => onValuesChange());
  const [isNumberCard, setIsNumberCard] = useState(false);
  const socketContext = useSocketContext();
  const amount = Form.useWatch('amount', form);
  const amountValue = amount || 0;
  const giftCardBalance = giftCard.data ? giftCard.resultGiftCard.available - amountValue : 0;

  const onScan = () => {
    giftCard.reset();
    setTimeout(() => { giftCard.inputRef.current?.focus(); }, 100);
  };

  const handleChangeGiftCardCode = (code: string) => {
    giftCard.setCode(code);
    setIsNumberCard(true);
  };

  const onKBFocus = (type: 'currency' | 'numberCard') => () => setIsNumberCard(type === 'numberCard');
  const fetchDetail = async (billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(res.data.data);
        form.setFieldValue('detail', detail);
        form.setFieldValue('amount', 0);
        delay(() => { giftCard.inputRef.current?.focus(); }, 100);
        const payments = {
          cash: 0,
          remaining: 0,
          chanceCash: 0,
          loyalty: 0,
          giftCard: 0,
          checkOthers: 0,
          card: 0,
          multiPay: 0,
          totalValue: detail.total,
          subTotal: detail.subTotal || 0,
          cardFee: 0,
          saleTax: detail.saleTax || 0,
          usedTax: detail.useTax || 0,
          discount: (detail.discount || 0) + (detail.discountItem || 0),
        } as ICustomerPaymentDetail;
        setTimeout(() => {
          socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
            billId,
            actor: TypePaymentActors.CASHIER,
            action: TypePaymentPassData.PASS_DATA,
            data: payments as ICustomerPaymentDetail,
          });
        }, 100);
        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (billId) => {
    socketContext.switchCusPayment(billId);
    giftCard.reset();
    form.resetFields();
    try {
      const detail = await fetchDetail(billId);
      if (detail) {
        setOpen(billId);
      }
    } catch (error) {
    }
  };

  const handleFinish = async (values: any) => {
    if (!giftCard.data) return;
    const data = values as { amount: number, detail: IBillInfoResData };
    const payment: ICashierGiftCardPayment = {
      amount: data.amount,
      paymentType: PAYMENT_TYPE.GIFT_CARD,
      giftCardId: giftCard.data?.id,
    };
    const val: IGiftCardPaymentValue = {
      amount: data.amount,
      giftCardId: giftCard.data?.id || '',
      data: giftCard.data,
      giftCardBalance,
      resultGiftCard: giftCard.resultGiftCard,
    };
    if (payment.amount < (data.detail.total || 0)) {
      setOpen('');
      cashierBtnMultiPayV3.current?.continueGiftCardPayWithMultiPay(data.detail.billId, val);
      return;
    }
    try {
      const isCompleted = await onPayment(data.detail.billId, payment);
      if (!isCompleted) return;
      setOpen('');
    } catch (error) { }
  };
  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  const onValuesChange = debounce(() => {
    const amountValue = form.getFieldValue('amount') || 0;
    const detail: IBillInfoResData = form.getFieldValue('detail') || null;
    const giftCardBalance = giftCard.data ? (giftCard?.resultGiftCard?.available || 0) - (amountValue || 0) : 0;
    const giftCardAvailable = giftCard?.resultGiftCard?.available || 0;

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
      billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.PASS_DATA,
      data: {
        cash: 0,
        remaining: 0,
        chanceCash: 0,
        loyalty: 0,
        giftCard: amountValue,
        giftCardBalance,
        giftCardAvailable,
        checkOthers: 0,
        card: 0,
        multiPay: 0,

        totalValue: detail?.total || 0,
        subTotal: detail?.subTotal || 0,
        cardFee: 0,
        saleTax: detail?.saleTax || 0,
        usedTax: detail?.useTax || 0,
        discount: (detail?.discount || 0) + (detail?.discountItem || 0),
      } as ICustomerPaymentDetail,
    });
  }, 1000);
  return (
    <Modal
      title=''
      open={!!billId}
      onClose={onClose}
      styles={{ header: { display: 'none' }, body: { maxHeight: 'unset' } }}
      width={'auto'} footer={null} className='modal-overflow-unset modal-non-opacity'
      okText='Redeem'
    >
      <ContainerModal form={form} onFinish={handleFinish} onValuesChange={onValuesChange}>
        <Form.Item name={'detail'} noStyle />
        <ContainerKeyboard>
          <HeaderModal>
            <div className="modal-title">gift cards</div>
          </HeaderModal>
          <Spin spinning={giftCard.loading}>
            <GiftCardNumberContainer>
              <Text content_content_2_16_medium color_text_3 text_center style={{ fontSize: 20 }}>
                Gift Card Number:
              </Text>
              <div className="input-selector-container">
                <Input
                  ref={giftCard.inputRef}
                  className='gift-card-number-input' size='small' value={giftCard.code} onChange={e => giftCard.setCode(e.target.value)}
                  onFocus={onKBFocus('numberCard')}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
                <button type='button' className='btn-scan' onClick={onScan}><img src={qrScanIcon} alt="qrScanIcon" className='qr-scan-icon' /></button>
              </div>
              {giftCard.msg && <div className="modal-warning">{giftCard.msg}</div>}
            </GiftCardNumberContainer>
          </Spin>

          <Box display='flex' flexDirection='column' gap='16px'>
            <Text content_content_2_16_medium color_text_3 text_center style={{ fontSize: 20 }}>
              Enter amount:
            </Text>
            <Form.Item noStyle name={'amount'}>
              <InputWithKeyboardStyled
                ref={dollarRef}
                inputMode='none'
                onFocus={onKBFocus('currency')}
                withForm
              />
            </Form.Item>
            <div hidden={!isNumberCard}>
              <NumberPadV2
                type={'NUMBER'}
                value={giftCard.code}
                controlRef={giftCard.inputRef}
                onChange={handleChangeGiftCardCode}
              />
            </div>
            <div hidden={isNumberCard}>
              <Form.Item noStyle name={'amount'}>
                <DollarAmountKeyboard
                  controlRef={dollarRef}
                />
              </Form.Item>
            </div>
          </Box>
          <FooterActions>
            <Button style={{ flex: 1 }} onClick={onClose} type_cancel><span>Close</span></Button>
            <Button disabled={!(amountValue && giftCard.data && (giftCardBalance >= 0))} style={{ flex: 1 }} onClick={() => form.submit()} type_primary><span>Redeem</span></Button>
          </FooterActions>
        </ContainerKeyboard>
        <GiftCardDetailContainer>
          <img src={giftCardBgIcon} alt="giftCardBgIcon" className='gift-card-bg-icon' />
          <div className="detail-item">
            <div className="detail-item-label">Gift Card Number</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                {giftCard.data?.giftCode || '--'}
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Balance</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                <FormItemValue name={'amount'} render={(amountValue) => {
                  const giftCardBalance = giftCard.data ? giftCard.resultGiftCard.available - (amountValue || 0) : 0;
                  return <>{formatCurrency(giftCardBalance > 0 ? giftCardBalance : 0)}</>;
                }} />
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Available</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                {formatCurrency(giftCard.resultGiftCard.available)}
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Used</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                {formatCurrency(giftCard.resultGiftCard.used)}
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Amount</div>
            <div className="detail-item-amount">
              <div className="gift-amount-box">
                <div className="detail-item-amount-text">
                  {formatCurrency(giftCard.resultGiftCard.amount)}
                </div>
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Last Used Date</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                {giftCard.data?.updatedDate ? moment(giftCard.data?.updatedDate).format('MM-DD-YYYY') : '__/__/____'}
              </div>
            </div>
          </div>
          <div className="detail-item">
            <div className="detail-item-label">Expiration</div>
            <div className="detail-item-amount">
              <div className="detail-item-amount-text">
                {giftCard.resultGiftCard.expireDateV2}
              </div>
            </div>
          </div>
        </GiftCardDetailContainer>
      </ContainerModal>
    </Modal >
  );
});
ModalGiftCardPayment.displayName = 'ModalGiftCardPayment';
export default ModalGiftCardPayment;
const ContainerModal = styled(Form)`
  display: flex;
`;
const HeaderModal = styled.div`
 border-radius: 5px 5px 0px 0px;
  background: #232F3E;
  padding: 12px 0;
  height: auto;
  .modal-title {
    color: #FFF;
    background: #232F3E;
    text-align: center;
    font-family: Poppins;
    font-size: 30px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
`;
const GiftCardNumberContainer = styled.div`
margin-bottom: 1rem;
margin-top: 1rem;
display: flex;
flex-direction: column;  
.modal-label {
  color: #64748B;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.modal-warning {
  color: #ff7d00;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.input-selector-container {
  display: flex;
  align-items: center;
  position: relative;
  height: 64px;
  margin: 0 40px;
  
  .qr-scan-icon {
    width: 64px;
    height: 64px;
  }

  .btn-scan {
    display: flex;
    width: 80px;
    height: 80px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    border-radius: 0px 4px 4px 0px;
    background: #FFF1F6;
    position: absolute;
    right: 0;
  }
}
.gift-card-number-input {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 30px;
  height: unset;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;
  box-shadow: none;
}
`;

const ContainerKeyboard = styled.div`
  width: 980px;

`;
const GiftCardDetailContainer = styled.div`
  display: flex;
  width: 567px;
  padding: 0px 16px;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
  position: relative;
  background: #FFF;
  overflow: hidden;
  .gift-card-bg-icon {
    display: flex;
    width: 450px;
    height: 450px;
    justify-content: center;
    align-items: center;
    position: absolute;
    right: 0px;
    bottom: -60px;
  }
  .detail-item {
    position: relative;
    display: flex;
    padding: 16px 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-bottom: 1.5px solid #86909C;
    flex: 1;
    .detail-item-label {
      color: #505050;
      text-align: center;
      font-family: Poppins;
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
    .detail-item-amount {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      .detail-item-amount-text {
        flex: 1 0 0;
        color: #2D6772;
        text-align: center;
        font-family: Poppins;
        font-size: 26px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: uppercase;
      }

      .gift-amount-box {
        display: flex;
        height: 56px;
        padding: 0px 16px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        background: #232F3E;
        .detail-item-amount-text {
          color: #FFF;
          text-align: center;
          font-family: Poppins;
          font-size: 26px;
          font-style: normal;
          font-weight: 600;
          line-height: normal;
        }
      }
    }
  }
`;
